import type { QueryClient } from '@tanstack/react-query';
import { entityChannel } from './channels';
import { createEntityPresence } from './entity-presence';
import type { ActiveRealtime } from './events';
import { createAvatarSource, type AvatarSource } from './peer-avatar';
import type { Peer } from './peers';
import { colorForTab } from './tab-color';

/** One other tab on an entity, as the Design Studio editor sees it. */
export type BridgePeer = {
  clientId: string;
  tab: string;
  user: { id: number; name: string; avatar?: string };
  focus?: { field: string; label?: string };
};

/** This tab's hold on one entity channel, from `joinEntity`. */
export interface HydraEntityPresence {
  /** Other tabs on the entity, including the user's own other tabs. */
  peers(): BridgePeer[];
  /** Called with the full peer list whenever it changes; returns unsubscribe. */
  subscribePeers(listener: (peers: BridgePeer[]) => void): () => void;
  /** Publishes this tab's focus on the entity; null clears it. */
  setFocus(field: string | null, label?: string): void;
  /** Clears the focus this handle set and drops its hold; the last leave unsubscribes. Idempotent. */
  leave(): void;
}

/** `window.__hydra__.realtime`: hydra provides it while connected to realtime, the editor consumes it. */
export interface HydraRealtimeBridge {
  version: 1;
  /** This tab's id. */
  selfTab: string;
  /** The colors everyone sees `tab` in, as resolved CSS values (never `var(...)`). */
  colorForTab(tab: string): { pointer: string; label: string };
  /**
   * Joins the channel of one entity (`type` matches `^[a-z_]{1,32}$`, `id` matches
   * `^[A-Za-z0-9_-]{1,64}$`), so focus follows the entity rather than the URL. Joins of the same
   * entity share one subscription; call `leave()` when done. An invalid type or id returns an
   * inert handle with no peers.
   */
  joinEntity(type: string, id: string): HydraEntityPresence;
}

const CSS_VAR = /^var\(\s*(--[^,)\s]+)\s*(?:,\s*(.+))?\)$/;

/** `var(--pluma-x)` becomes the color it currently resolves to, so a non-Pluma app can paint with it. */
function resolveCssValue(value: string): string {
  const match = CSS_VAR.exec(value.trim());
  if (match === null) return value;
  const resolved = getComputedStyle(document.documentElement)
    .getPropertyValue(match[1] ?? '')
    .trim();
  return resolved !== '' ? resolved : (match[2]?.trim() ?? value);
}

function toBridgePeer({ clientId, tab, user, focus }: Peer, avatars: AvatarSource): BridgePeer {
  const peer: BridgePeer = { clientId, tab, user: { id: user.id, name: user.name } };
  const avatar = avatars.lookup(user.id) ?? user.avatar;
  if (avatar !== undefined) peer.user.avatar = avatar;
  if (focus?.field != null) {
    peer.focus = focus.label === undefined ? { field: focus.field } : { field: focus.field, label: focus.label };
  }
  return peer;
}

/** Everything a bridge listener can see about a peer, so an unchanged list isn't re-sent. */
const peerKey = ({ clientId, tab, user, focus }: BridgePeer) =>
  JSON.stringify([clientId, tab, user.id, user.name, user.avatar, focus?.field, focus?.label]);

const samePeers = (a: BridgePeer[], b: BridgePeer[]) =>
  a.length === b.length && a.every((p, i) => b[i] !== undefined && peerKey(p) === peerKey(b[i]));

const INERT_ENTITY: HydraEntityPresence = {
  peers: () => [],
  subscribePeers: () => () => {},
  setFocus: () => {},
  leave: () => {},
};

type BridgeOptions = ActiveRealtime & { queryClient: QueryClient };

/** Builds the bridge for one realtime connection. */
export function createRealtimeBridge({
  transport,
  environmentId,
  tab,
  queryClient,
}: BridgeOptions): HydraRealtimeBridge {
  const avatarWaiters = new Set<() => void>();
  const avatars = createAvatarSource(queryClient, () => avatarWaiters.forEach((wake) => wake()));

  return {
    version: 1,
    selfTab: tab,
    colorForTab: (other) => {
      const { pointer, label } = colorForTab(other);
      return { pointer: resolveCssValue(pointer), label: resolveCssValue(label) };
    },
    joinEntity: (type, id) => {
      const channel = entityChannel(environmentId, type, id);
      if (channel === null) return INERT_ENTITY;
      const presence = createEntityPresence(transport, channel);
      let last: BridgePeer[] = [];
      // The same array until something a listener can see changes.
      const peers = () => {
        const list = presence.store.getList();
        avatars.ensure(list);
        const next = list.map((peer) => toBridgePeer(peer, avatars));
        if (!samePeers(last, next)) last = next;
        return last;
      };
      return {
        peers,
        subscribePeers: (listener) => {
          let before = peers();
          const check = () => {
            const next = peers();
            if (next === before) return;
            before = next;
            listener(next);
          };
          avatarWaiters.add(check);
          const unsubscribe = presence.store.subscribe(check);
          return () => {
            avatarWaiters.delete(check);
            unsubscribe();
          };
        },
        setFocus: presence.setFocus,
        leave: presence.release,
      };
    },
  };
}
